import { AlertTriangle, Check, EyeOff } from "lucide-react";
import { type ReactElement } from "react";

import { cn } from "../utils/cn";

export interface StrengthsWeaknessesProps {
  strengths?: readonly string[];
  weaknesses?: readonly string[];
  /** The Anonym-Ansicht withheld the list: its anonymized view is missing (#140, #172). */
  strengthsHidden?: boolean;
  weaknessesHidden?: boolean;
  className?: string;
}

export function StrengthsWeaknesses({
  strengths = [],
  weaknesses = [],
  strengthsHidden = false,
  weaknessesHidden = false,
  className,
}: StrengthsWeaknessesProps): ReactElement {
  // Stacks when the component itself is narrow (#148), wherever it is placed. Only words
  // of 12+ letters hyphenate, so „Hybrid" or a company name stays whole.
  return (
    <div className={cn("@container", className)}>
      <div className="grid grid-cols-1 gap-4 @lg:grid-cols-2">
        <section
          aria-label="Stärken"
          className="rounded-[10px] border border-success-200 bg-success-50 px-4 py-3.5"
        >
          <header className="mb-2.5 flex items-center gap-2">
            <span className="inline-flex h-5.5 w-5.5 items-center justify-center rounded-full bg-success-500 text-neutral-0">
              <Check size={13} strokeWidth={3} />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-success-700">
              Stärken
            </span>
          </header>
          <ul className="flex flex-col gap-2">
            {strengthsHidden ? (
              <HiddenItem />
            ) : strengths.length === 0 ? (
              <li className="text-xs italic text-text-tertiary">Keine Stärken hinterlegt.</li>
            ) : (
              strengths.map((s) => (
                <li key={s} className="flex gap-2 text-[13px] leading-snug">
                  <span className="mt-0.5 font-bold text-success-600">+</span>
                  <span className="min-w-0 text-neutral-800 hyphens-auto wrap-break-word [hyphenate-limit-chars:12_5_5]">
                    {s}
                  </span>
                </li>
              ))
            )}
          </ul>
        </section>

        <section
          aria-label="Schwächen"
          className="rounded-[10px] border border-danger-200 bg-danger-50 px-4 py-3.5"
        >
          <header className="mb-2.5 flex items-center gap-2">
            <span className="inline-flex h-5.5 w-5.5 items-center justify-center rounded-full bg-danger-500 text-neutral-0">
              <AlertTriangle size={13} strokeWidth={3} />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-danger-700">
              Schwächen
            </span>
          </header>
          <ul className="flex flex-col gap-2">
            {weaknessesHidden ? (
              <HiddenItem />
            ) : weaknesses.length === 0 ? (
              <li className="text-xs italic text-text-tertiary">Keine Schwächen hinterlegt.</li>
            ) : (
              weaknesses.map((s) => (
                <li key={s} className="flex gap-2 text-[13px] leading-snug">
                  <span className="mt-0.5 font-bold text-danger-600">−</span>
                  <span className="min-w-0 text-neutral-800 hyphens-auto wrap-break-word [hyphenate-limit-chars:12_5_5]">
                    {s}
                  </span>
                </li>
              ))
            )}
          </ul>
        </section>
      </div>
    </div>
  );
}

// Same wording as HIDDEN_TEXT in apps/web/app/anonym/copy.ts (ui can't import from the app).
function HiddenItem(): ReactElement {
  return (
    <li className="flex items-center gap-2 text-[13px] leading-snug text-text-secondary">
      <EyeOff size={14} aria-hidden="true" className="shrink-0" />
      Text in der Anonym-Ansicht verborgen.
    </li>
  );
}
